import { Film, Menu, X, Sun, Moon, Search, Palette, Check, Rss } from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTheme, COLOR_THEMES, type ColorKey } from '@/lib/theme'
import JsonLd, { websiteSchema } from '@/components/JsonLd'
import ScrollToTop from '@/components/ScrollToTop'
import { subscribeToNewsletter } from '@/lib/api'
import { sanitizeEmail } from '@/lib/sanitize'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false)
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterMsg, setNewsletterMsg] = useState('')
  const colorPickerRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  const { theme, toggleTheme, colorKey, setColorTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Browse', path: '/browse' },
    { label: 'Most Picked', path: '/leaderboard' },
  ]

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  // Close color picker on outside click or Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setIsColorPickerOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsColorPickerOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  // Close color picker/menu, reset scroll, and move keyboard focus to main on route change
  useEffect(() => {
    setIsColorPickerOpen(false)
    setIsMenuOpen(false)
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    // Move focus to main content without nudging scroll position
    mainRef.current?.focus({ preventScroll: true })
  }, [location.pathname])

  // '/' keyboard shortcut — navigate to search (skip if user is already typing in an input)
  const handleSlashKey = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    if (e.key === '/') {
      e.preventDefault()
      navigate('/search')
    }
  }, [navigate])

  useEffect(() => {
    document.addEventListener('keydown', handleSlashKey)
    return () => document.removeEventListener('keydown', handleSlashKey)
  }, [handleSlashKey])

  async function handleNewsletterSubmit(e: React.FormEvent) {
    e.preventDefault()
    const email = sanitizeEmail(newsletterEmail)
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    if (!valid) {
      setNewsletterMsg('Enter a valid email address.')
      return
    }
    try {
      const res = await subscribeToNewsletter(email)
      if (!res.ok) throw new Error('subscribe failed')
      setNewsletterEmail('')
      setNewsletterMsg('Subscribed successfully.')
    } catch {
      setNewsletterMsg('Could not subscribe right now. Please try again.')
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <JsonLd data={websiteSchema()} />

      {/* Skip navigation link — a11y */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-gray-200 dark:border-slate-800 sticky top-0 z-50 transition-colors duration-200" role="banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group" aria-label="Celebrity Picks — Home">
              <div className="p-1.5 rounded-lg bg-primary-600 group-hover:bg-primary-700 transition-colors" aria-hidden="true">
                <Film className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-slate-50 tracking-tight">
                Celebrity<span className="text-primary-600 dark:text-primary-400"> Picks</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-1" aria-label="Main navigation">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive(item.path)
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-200'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right side controls */}
            <div className="flex items-center gap-0.5 sm:gap-1">

              {/* Search — title shows '/' shortcut hint */}
              <Link
                to="/search"
                className="p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200 transition-all duration-150"
                aria-label="Search (press / anywhere)"
                title="Search (press / anywhere)"
              >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
              </Link>

              {/* Color Picker */}
              <div className="relative" ref={colorPickerRef}>
                <button
                  onClick={() => setIsColorPickerOpen(prev => !prev)}
                  aria-expanded={isColorPickerOpen}
                  aria-haspopup="listbox"
                  className={`p-2 rounded-lg transition-all duration-150 ${
                    isColorPickerOpen
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200'
                  }`}
                  aria-label="Choose color theme"
                  title="Choose color theme"
                >
                  <Palette className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>

                {/* Dropdown */}
                {isColorPickerOpen && (
                  <div role="listbox" aria-label="Color themes" className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden z-50 animate-fade-in">
                    <div className="px-3 pt-3 pb-1.5">
                      <p className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest">
                        Color Theme
                      </p>
                    </div>

                    <div className="px-1.5 pb-1.5 space-y-0.5">
                      {(Object.entries(COLOR_THEMES) as [ColorKey, typeof COLOR_THEMES[ColorKey]][]).map(([key, ct]) => {
                        const isSelected = colorKey === key
                        return (
                          <button
                            key={key}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => { setColorTheme(key); setIsColorPickerOpen(false) }}
                            className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-left transition-colors duration-100 ${
                              isSelected
                                ? 'bg-gray-100 dark:bg-slate-700'
                                : 'hover:bg-gray-50 dark:hover:bg-slate-700/60'
                            }`}
                          >
                            {/* Color swatch */}
                            <span
                              aria-hidden="true"
                              className={`w-5 h-5 rounded-full flex-shrink-0 ring-offset-2 ring-offset-white dark:ring-offset-slate-800 transition-all ${
                                isSelected ? 'ring-2' : 'ring-0'
                              }`}
                              style={{
                                backgroundColor: ct.preview,
                                boxShadow: isSelected ? `0 0 0 2px white, 0 0 0 4px ${ct.preview}` : undefined,
                              }}
                            />
                            <span className={`text-sm font-medium flex-1 ${
                              isSelected
                                ? 'text-gray-900 dark:text-slate-100'
                                : 'text-gray-700 dark:text-slate-300'
                            }`}>
                              {ct.name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Dark / Light Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200 transition-all duration-150"
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4 sm:h-5 sm:w-5 text-accent-400" aria-hidden="true" />
                ) : (
                  <Moon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                )}
              </button>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-nav"
              >
                {isMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {isMenuOpen && (
            <nav id="mobile-nav" className="md:hidden py-3 border-t border-gray-100 dark:border-slate-800" aria-label="Mobile navigation">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" ref={mainRef} className="flex-1 outline-none" tabIndex={-1}>
        {children}
      </main>

      {/* Scroll to top button */}
      <ScrollToTop />

      {/* Footer */}
      <footer className="bg-slate-900 dark:bg-slate-950 text-slate-300 py-12 border-t border-slate-800" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-1.5 rounded-lg bg-primary-600" aria-hidden="true">
                  <Film className="h-4 w-4 text-white" />
                </div>
                <span className="text-lg font-bold text-white">Celebrity Picks</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                Discover movies and TV shows recommended by the world's most interesting people. Every pick is source-verified.
              </p>
              <div className="flex gap-3">
                <Link to="/about" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">About</Link>
                <a href="/rss.xml" className="text-xs text-slate-500 hover:text-slate-300 transition-colors inline-flex items-center gap-1">
                  <Rss className="h-3 w-3 text-orange-400" aria-hidden="true" />RSS
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Explore</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/browse" className="text-slate-400 hover:text-white transition-colors">All Recommendations</Link></li>
                <li><Link to="/leaderboard" className="text-slate-400 hover:text-white transition-colors">Most Picked</Link></li>
                <li><Link to="/genres" className="text-slate-400 hover:text-white transition-colors">Browse by Genre</Link></li>
                <li><Link to="/search" className="text-slate-400 hover:text-white transition-colors">Search</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Newsletter</h3>
              <p className="text-sm text-slate-400 mb-4 leading-relaxed">
                Get weekly curated picks from the world's most interesting minds.
              </p>
              <form className="flex gap-2" onSubmit={handleNewsletterSubmit} aria-label="Newsletter signup">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(sanitizeEmail(e.target.value))}
                  placeholder="your@email.com"
                  aria-label="Email address"
                  maxLength={254}
                  autoComplete="email"
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button type="submit" className="btn-primary text-sm whitespace-nowrap">Subscribe</button>
              </form>
              {newsletterMsg && <p className="text-xs text-slate-400 mt-2">{newsletterMsg}</p>}
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                By subscribing, you agree to our <Link to="/privacy" className="underline hover:text-slate-300">Privacy Policy</Link> and <Link to="/terms" className="underline hover:text-slate-300">Terms</Link>.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-slate-500">
            <span>© {new Date().getFullYear()} Celebrity Picks. All rights reserved.</span>
            <nav aria-label="Footer navigation" className="flex flex-wrap items-center justify-center gap-3 text-xs">
              <Link to="/about" className="hover:text-slate-300">About</Link>
              <Link to="/privacy" className="hover:text-slate-300">Privacy</Link>
              <Link to="/terms" className="hover:text-slate-300">Terms</Link>
              <Link to="/affiliate-disclosure" className="hover:text-slate-300">Affiliate Disclosure</Link>
              <Link to="/contact" className="hover:text-slate-300">Contact</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  )
}
